import { useTranslation } from "react-i18next";

import { Spinner } from "src/core/components/custom-ui/spinner";
import { EntityDetailHeader } from "src/core/components/folder/entities/detail/EntityDetailHeader";
import { EntityMatchList } from "src/core/components/folder/entities/detail/EntityMatchList";
import { EntityNoAlertMessage } from "src/core/components/folder/entities/detail/EntityNoAlertMessage";
import { EntityDetailPreview } from "src/core/components/folder/entities/preview/EntityDetailPreview";
import { Button } from "src/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/core/components/ui/dialog";
import { useEntityDetails } from "src/core/hooks/folder/useEntityDetails";
import { MappedEntity } from "src/core/types/entity";

type EntityDetailsModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  entity?: MappedEntity;
};

export const EntityDetailsModal = ({ open, setOpen, entity }: EntityDetailsModalProps) => {
  const { t } = useTranslation();

  const {
    isPep,
    isSanction,
    isLoading,
    eReputationMatchResults,
    selectedMatch,
    validationError,
    setValidationError,
    updatingEReputation,
    onSubmit,
    control,
    reset,
    handleSubmit,
    setValue,
    watch,
    handleSelectMatch,
    isReadOnly,
    isPPH,
  } = useEntityDetails({ entity, open, setOpen });

  if (!entity) {
    return null;
  }

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={setOpen} variant="entity">
        <DialogContent
          className="modal-entity-details"
          hideCloseButton
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="modal-entity-details-header">
            <DialogTitle>{t("modal.entityDetails.title")}</DialogTitle>
            <DialogDescription className="sr-only">
              {t("modal.entityDetails.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-center h-full">
            <Spinner />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen} variant="entity">
      <DialogContent
        className="modal-entity-details"
        hideCloseButton
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="modal-entity-details-header">
          <DialogTitle>{t("modal.entityDetails.title")}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("modal.entityDetails.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 px-20">
          <EntityDetailHeader entity={entity} />

          <div className="grid grid-cols-2">
            {!isPep && !isSanction && (
              <div className="col-span-2">
                <EntityNoAlertMessage entity={entity} />
              </div>
            )}

            {(isPep || isSanction) && (
              <>
                <div className="col-span-1">
                  <div className="flex flex-col bg-gray-xxlight border border-border p-10 h-[calc(80vh-145px)]">
                    <EntityMatchList
                      results={eReputationMatchResults}
                      selectedMatch={selectedMatch}
                      onSelect={handleSelectMatch}
                      control={control}
                      isLoading={updatingEReputation || isLoading}
                      setValue={setValue}
                      watch={watch}
                      isReadOnly={isReadOnly}
                    />
                  </div>
                </div>

                <div className="col-span-1">
                  <div className="flex flex-col bg-gray-xxlight border border-border p-10 h-[calc(80vh-145px)]">
                    <EntityDetailPreview selectedMatch={selectedMatch} isPPH={isPPH} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <DialogFooter>
          {eReputationMatchResults.length > 0 ? (
            <>
              <div className="grid grid-cols-12">
                <div className="col-span-5"></div>
                <div className=" col-span-2 flex items-center gap-4 w-full justify-center">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      reset();
                      setOpen(false);
                      setValidationError(null);
                    }}
                  >
                    {t("modal.entityDetails.cancel")}
                  </Button>

                  <Button
                    variant="default"
                    onClick={handleSubmit(onSubmit)}
                    loading={updatingEReputation}
                    disabled={isReadOnly}
                  >
                    {t("modal.entityDetails.save")}
                  </Button>
                </div>
                <div className="col-span-5 flex items-center justify-center">
                  {validationError && (
                    <span className="text-alert font-light whitespace-pre-line ml-10">
                      {t(validationError)}
                    </span>
                  )}
                </div>
              </div>
            </>
          ) : (
            <Button
              variant="default"
              onClick={(event) => {
                event.preventDefault();
                setOpen(false);
              }}
            >
              {t("modal.entityDetails.close")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
